import axios from "axios";
import { onError } from "apollo-link-error";
import { fromPromise } from "apollo-link";
import { concat, createHttpLink, split } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { potionsLocalStorage } from "@localStorage";
import * as Sentry from "@sentry/browser";

let isRefreshing = false;
let pendingRequests = [];

const getNewToken = () => {
  if (!potionsLocalStorage.refreshToken) {
    potionsLocalStorage.clear();
    if (!window.location.href.includes("login")) {
      window.location.href = `${window.location.origin}/login`;
    }
  }
  return axios({
    method: "post",
    url: `${process.env.REACT_APP_API_URL}api/auth/token`,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    data: `grant_type=refresh_token&refresh_token=${potionsLocalStorage.refreshToken}`,
  }).then((res) => {
    if (res?.data?.access_token) {
      potionsLocalStorage.set("token", res?.data.access_token);
      potionsLocalStorage.set("refreshToken", res?.data.refresh_token);
      return res?.data.access_token;
    } else {
      potionsLocalStorage.clear();
      window.location.href = `${window.location.origin}/login`;
    }
  });
};

const resolvePendingRequests = () => {
  pendingRequests.map((callback) => callback());
  pendingRequests = [];
};

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (networkError?.statusCode === 401) {
      let forward$;

      if (!isRefreshing) {
        isRefreshing = true;
        forward$ = fromPromise(
          getNewToken()
            .then(({ accessToken, refreshToken }) => {
              resolvePendingRequests();
              return accessToken;
            })
            .catch((error) => {
              pendingRequests = [];
              potionsLocalStorage.clear();
              if (!window.location.href.includes("login")) {
                window.location.href = `${window.location.origin}/login`;
              }
              return;
            })
            .finally(() => {
              isRefreshing = false;
            })
        ).filter((value) => Boolean(value));
      }
      forward$ = fromPromise(
        new Promise((resolve) => {
          pendingRequests.push(() => resolve());
        })
      );

      return forward$.flatMap(() => forward(operation));
    }
    // IF USER NOT R&M ACCOUNT
    else if (networkError?.statusCode === 499) {
      if (window.location.href !== `${window.location.origin}/not_account`) {
        window.location.href = `${window.location.origin}/not_account`;
      }
      return;
    } else if (networkError) {
      Sentry.captureException(new Error(`[Network error]: ${networkError}`), {
        extra: {
          operationName: operation.operationName,
          variables: operation.variables,
        },
      });
    }
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        Sentry.captureException(
          new Error(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
          ),
          {
            extra: {
              operationName: operation.operationName,
              variables: operation.variables,
            },
          }
        );
      });
    }
  }
);

const httpLink = createHttpLink({
  uri: `${process.env.REACT_APP_API_URL}graphql`,
  credentials: "include",
});

const uploadLink = createUploadLink({
  uri: `${process.env.REACT_APP_API_URL}graphql`,
  credentials: "include",
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  let token;
  try {
    token = potionsLocalStorage.token;
  } catch (e) {
    if (!token) token = undefined;
  }
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const conditionalLink = split(
  ({ getContext }) => {
    const context = getContext();
    return context.hasUpload || context.useMultipart;
  },
  uploadLink, 
  httpLink 
);

export const apolloLink = concat(errorLink, concat(authLink, conditionalLink));